import React, { createContext, useState, useEffect } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import useAxios from "../../hooks/useAxios";

//helpers and hooks
import { FormatFiltersQuery, updateUrlParams, parseUrlParams } from "./helpers";

const Context = createContext();

//Filters API:
//https://cneheritage.local/wp-json/cneheritage/v1/filters?type=cne-media

// Handles global states and state updates for the entire Explore index app

export const AppProvider = ({ children }) => {
	// used to display the selected filters, it also accounts for sharable filters
	const [selectedFilters, setSelectedFilters] = useState(
		location.search ? parseUrlParams(location.search) : []
	);
	const [urlHash] = useState(
		location.hash ? window.location.hash.replace("#", "") : null
	);
	const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
	const [selectedMediaItem, setSelectedMediaItem] = useState({});
	const [currentPageNumber, setCurrentPageNumber] = useState(1);
	const [mediaItemsUrl, setMediaItemsUrl] = useState(
		`cne-media?page=${currentPageNumber}${FormatFiltersQuery(selectedFilters)}`
	);

	// fetch all the filters from the API
	const { response: filters } = useAxios("filters?type=cne-media");
	const { response: mediaItems, totalPages } = useAxios(mediaItemsUrl);

	useEffect(() => {
		// request doesnt get called twice on initial because the string should match the
		setMediaItemsUrl(
			`cne-media?page=${currentPageNumber}${FormatFiltersQuery(
				selectedFilters
			)}`
		);
	}, [selectedFilters, currentPageNumber]);

	// if theres a hash present in the url we make a call to the api to get
	useEffect(() => {
		if (urlHash) {
			let baseUrl = window.origin;
			let reqUrl = `${baseUrl}/wp-json/cneheritage/v1/item?id=${urlHash}`;
			axios
				.get(reqUrl)
				.then((response) => {
					return toggleSidePanel(response.data.data[0]);
				})
				.catch((error) => {
					return error.message;
				});
		}
	}, [urlHash]);

	useEffect(() => {
		// every time the selectedFilters update we update the browser URL to match
		updateUrlParams(selectedFilters);
	}, [selectedFilters]);

	// onClick handler for selecting and deselecting a filter
	// this function formats the selectedFilters into a specific data structure
	// that we'll use for the constructing the filters query for the api call
	const handleFilterSelect = (filter) => {
		let selectedFiltersCopy = [...selectedFilters];

		// we get the taxonomy index in the array
		let existingTaxonomyIndex = selectedFiltersCopy.findIndex(
			(t) => t.taxonomy == filter.taxonomy
		);

		// check if taxonomy exists
		if (existingTaxonomyIndex >= 0) {
			// get index of the child filter
			let existingFilterIndex = selectedFiltersCopy[
				existingTaxonomyIndex
			].filters.findIndex((f) => f.slug == filter.filters[0].slug);

			let childFilters = selectedFiltersCopy[existingTaxonomyIndex].filters;

			// check if child filter exists
			if (existingFilterIndex >= 0) {
				// if child filter exists we remove it using the index
				childFilters.splice(existingFilterIndex, 1);

				// if there's no child filters left we remove the whole taxonomy
				if (childFilters.length == 0) {
					selectedFiltersCopy.splice(existingTaxonomyIndex, 1);
				}
			} else {
				// we dont have the child filter so we push it into the array of child filters inside taxonomy object
				childFilters.push(filter.filters[0]);
			}
		} else {
			selectedFiltersCopy = [...selectedFiltersCopy, filter];
		}

		// we reset the page everytime a filter is selected and deselected
		setCurrentPageNumber(1);
		setSelectedFilters(selectedFiltersCopy);
	};

	// onClick handler for selecting and deselecting a filter
	const clearAllFilters = () => {
		setSelectedFilters([]);
		setCurrentPageNumber(1);
		window.history.pushState({}, "", window.location.pathname);
	};

	const toggleSidePanel = (mediaItem) => {
		if (mediaItem) {
			setIsSidePanelOpen(true);
			setSelectedMediaItem(mediaItem);
			// every time we toggle the sidepanel we call update the URL
			updateUrlParams(selectedFilters, mediaItem);
		} else {
			setIsSidePanelOpen(false);
			setSelectedMediaItem({});
			updateUrlParams(selectedFilters); // we remove the media item id from the URL
		}
	};
	// handle scroll when side panel is opened and closed
	useEffect(() => {
		if (isSidePanelOpen) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "scroll";
		}
	}, [isSidePanelOpen]);
	

	const scrollToTop = () => {
		return window.scrollTo({ top: 0, left: 0, behaviour: "smooth" });
	}

	// Pagination helpers actions
	const handleIncrementPage = () => {
		if (currentPageNumber < totalPages.length) {
			setCurrentPageNumber((currentNumber) => currentNumber + 1);
			scrollToTop();
		}
	};
	const handleDecrementPage = () => {
		if (currentPageNumber > 1) {
			setCurrentPageNumber((currentNumber) => currentNumber - 1);
			scrollToTop();
		}
	};
	const handleButtonPageSelect = (buttonNumber) => {
		setCurrentPageNumber(buttonNumber);
		scrollToTop();
	};

	const data = {
		selectedFilters,
		filters,
		mediaItems,
		isSidePanelOpen,
		selectedMediaItem,
		totalPages,
		currentPageNumber,
	};

	const actions = {
		handleFilterSelect,
		clearAllFilters,
		toggleSidePanel,
		handleIncrementPage,
		handleDecrementPage,
		handleButtonPageSelect,
	};

	return (
		<Context.Provider value={{ actions, data }}>{children}</Context.Provider>
	);
};

AppProvider.propTypes = {
	children: PropTypes.node,
};
export default Context;
